/**
 * Metric Catalog Metadata section — rendered on the Review step (step 6), below
 * the assembled YAML config.
 *
 * ## Why this exists
 *
 * The YAML `base_metrics` / `derived_metrics` blocks are seed metadata that
 * Bluey's stored procedure NEVER reads. At query time `sp_bluey_common_report`
 * resolves every requested metric against two Redshift TABLES named by the
 * config's `base_metrics_catalog` / `derived_metrics_catalog` fields, raising
 * `Derived metric not found` when a row is missing. So the config alone does not
 * make a tool work — these two tables must exist and be populated. See
 * `catalogRows.ts` for the SP citations.
 *
 * Two tabs, each containing the SAME two catalog tables (a preview of the exact
 * rows that will land in Redshift) above that tab's action:
 *   - "Write to Redshift" — creates + populates the tables via the backend.
 *     Create-only: the API refuses (409) if either table already has rows.
 *   - "Download CSVs"    — the same rows as CSV, in either the AutoFlip-loader
 *     flavor (for committing to the Bluey package) or plain CSV (for a manual
 *     `COPY`). Pure client-side, so it works even with no backend — and it leads
 *     the tab order when the backend is unavailable.
 *
 * Rows come from the shared `buildCatalogRows`, the same builder that feeds the
 * YAML — so what is previewed, downloaded, and written can never diverge from
 * what the config declares.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  SpaceBetween,
  Container,
  Header,
  Tabs,
  Table,
  Button,
  Box,
  Alert,
  StatusIndicator,
  FormField,
  Input,
  Select,
} from '@amzn/awsui-components-react';

import {
  BaseCatalogRow,
  baseCatalogCsv,
  buildCatalogRows,
  CATALOG_SCHEMA_PATTERN,
  DerivedCatalogRow,
  derivedCatalogCsv,
  findVersionAxisErrors,
  resolveCatalogTables,
  validateCatalogRows,
} from '../catalogRows';
import { OnboardingApiError, writeCatalogs } from '../onboardingApi';
import { OnboardingAction, SECRET_ENV_OPTIONS, SecretEnv } from '../onboardingReducer';
import { SECRET_ARN_PATTERN } from '../onboardingValidators';
import { CATALOG_NOT_EMPTY_CODE, OnboardingConfig, WriteCatalogsResponse } from '../types';

/**
 * CSV flavors. These are NOT interchangeable: the AutoFlip directive is a
 * comment block the loader pipeline consumes, but Redshift `COPY` has no comment
 * syntax and would ingest those lines as a malformed data row.
 */
type CsvFormat = 'autoflip' | 'plain';

const CSV_FORMAT_OPTIONS: Array<{ value: CsvFormat; label: string }> = [
  { value: 'autoflip', label: 'AutoFlip loader (for the Bluey package)' },
  { value: 'plain', label: 'Plain CSV (for a manual COPY / spreadsheet)' },
];

/** Triggers a browser download of `content` as `fileName`. */
function downloadFile(fileName: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/** Renders a cell value, showing an em dash for a null/blank field. */
function cellText(value: string | null): string {
  return value === null || value === '' ? '—' : value;
}

const BASE_COLUMN_DEFINITIONS = [
  { id: 'metric_key', header: 'metric_key', cell: (r: BaseCatalogRow) => cellText(r.metric_key) },
  { id: 'expr_sql', header: 'expr_sql', cell: (r: BaseCatalogRow) => cellText(r.expr_sql) },
  { id: 'agg', header: 'agg', cell: (r: BaseCatalogRow) => cellText(r.agg) },
  {
    id: 'filter_condition',
    header: 'filter_condition',
    cell: (r: BaseCatalogRow) => cellText(r.filter_condition),
  },
];

const DERIVED_COLUMN_DEFINITIONS = [
  {
    id: 'derived_metric_key',
    header: 'derived_metric_key',
    cell: (r: DerivedCatalogRow) => cellText(r.derived_metric_key),
  },
  {
    id: 'base_metrics_csv',
    header: 'base_metrics_csv',
    cell: (r: DerivedCatalogRow) => cellText(r.base_metrics_csv),
  },
  { id: 'expr_sql', header: 'expr_sql', cell: (r: DerivedCatalogRow) => cellText(r.expr_sql) },
  {
    id: 'description',
    header: 'description',
    cell: (r: DerivedCatalogRow) => cellText(r.description),
  },
];

export interface CatalogMetadataSectionProps {
  config: OnboardingConfig;
  dispatch: React.Dispatch<OnboardingAction>;
  /**
   * Whether the service endpoint was substituted at deploy time. The write tab
   * needs the backend; the download tab is pure client-side and always works.
   */
  backendAvailable?: boolean;
}

const CatalogMetadataSection: React.FC<CatalogMetadataSectionProps> = ({
  config,
  dispatch,
  backendAvailable = false,
}) => {
  const [writeEnv, setWriteEnv] = useState<SecretEnv>('test');
  const [csvFormat, setCsvFormat] = useState<CsvFormat>('autoflip');
  const [writing, setWriting] = useState(false);
  // Lets the user dismiss the version-axis warning for the current visit. A plain
  // boolean is enough: base metrics are edited on the Metrics step, and the wizard
  // renders only the active step (OnboardingWizardPage), so leaving to edit metrics
  // unmounts this component and returning remounts it with the dismissal reset —
  // a newly introduced version-axis metric re-warns. Within a single visit only
  // SET_CATALOG_SCHEMA can change (it doesn't feed version-axis detection), so not
  // resetting on that is deliberate: no need to re-nag after an acknowledged risk.
  const [versionAxisDismissed, setVersionAxisDismissed] = useState(false);
  /**
   * The outcome of the last write, tagged with the target + rows it applies to
   * (see `writeIdentity` below) so it can be discarded the moment either
   * changes. Success and failure live in one piece of state because they are
   * mutually exclusive — a write cannot both succeed and fail.
   */
  const [writeOutcome, setWriteOutcome] = useState<{
    identity: string;
    result?: WriteCatalogsResponse;
    error?: { message: string; code: string };
  } | null>(null);

  // Rows + table names are derived from `config`, so they always reflect the
  // user's current metrics (including edits made after a previous write).
  const rows = useMemo(() => buildCatalogRows(config), [config]);
  const tables = useMemo(() => resolveCatalogTables(config), [config]);
  const rowWarnings = useMemo(() => validateCatalogRows(rows), [rows]);
  // A version-axis reference in a base row's `expr_sql` or `filter_condition`
  // makes the metric silently return 0 at query time. This now WARNS rather than
  // blocks — Next is no longer gated on it, and the write button is not disabled
  // by it. The caveat it flags is still real: the writer is create-only with no
  // update/delete endpoint, so a bad row is unrepairable through this API. The
  // dismissible warning below is the only safeguard left, so its copy is explicit
  // about that consequence.
  const versionAxisErrors = useMemo(() => findVersionAxisErrors(rows), [rows]);

  // A write outcome describes ONE specific target + row set. If the user then
  // edits the schema or goes back and changes metrics, the banner would still
  // claim success for rows that are no longer what would be written — so stamp
  // the outcome with an identity and discard it when that identity changes.
  // Derived during render (not an effect) so the stale banner never paints once.
  const writeIdentity = `${tables.baseQualified}|${tables.derivedQualified}|${JSON.stringify(rows.base)}|${JSON.stringify(rows.derived)}`;
  if (writeOutcome !== null && writeOutcome.identity !== writeIdentity) {
    setWriteOutcome(null);
  }
  const activeOutcome = writeOutcome?.identity === writeIdentity ? writeOutcome : null;
  const writeResult = activeOutcome?.result ?? null;
  const writeError = activeOutcome?.error ?? null;
  // Already written for this exact target + rows: block a second click, which
  // would 409 and replace the success banner with a scary warning.
  const alreadyWritten = writeResult !== null;

  const baseFileName = `${tables.baseTable}.csv`;
  const derivedFileName = `${tables.derivedTable}.csv`;

  const withAutoFlipHeader = csvFormat === 'autoflip';
  const csvOptions = { autoFlipHeader: withAutoFlipHeader };
  const baseCsv = useMemo(
    () => baseCatalogCsv(rows.base, csvOptions),
    [rows.base, withAutoFlipHeader],
  );
  const derivedCsv = useMemo(
    () => derivedCatalogCsv(rows.derived, csvOptions),
    [rows.derived, withAutoFlipHeader],
  );

  // Trim ONCE and send the trimmed value: validating `.trim()` but POSTing the raw
  // string would let a copy-pasted ARN with trailing whitespace pass the gate and
  // then fail server-side (Secrets Manager rejects the padded ARN).
  const secretArn = config.database.secretArns[writeEnv].trim();
  const schemaValid = CATALOG_SCHEMA_PATTERN.test(tables.schema);
  const hasRows = rows.base.length > 0 || rows.derived.length > 0;
  const canWrite =
    backendAvailable &&
    SECRET_ARN_PATTERN.test(secretArn) &&
    schemaValid &&
    hasRows &&
    !alreadyWritten &&
    // Guard against a double-click issuing concurrent writes: the second call
    // would race the first and 409 against the rows the first one just inserted.
    !writing;

  const handleWrite = useCallback(async () => {
    if (!canWrite) return;

    setWriting(true);
    setWriteOutcome(null);

    // Snapshot the identity BEFORE awaiting. The outcome is tagged with the
    // target + rows as they were at submit time, so if the user edits metrics or
    // the schema mid-flight the result is recognized as stale and discarded
    // rather than being attributed to the new rows.
    const submittedIdentity = writeIdentity;

    try {
      const response = await writeCatalogs({
        secret_arn: secretArn,
        schema_name: tables.schema,
        base_table: tables.baseTable,
        derived_table: tables.derivedTable,
        base_metrics: rows.base,
        derived_metrics: rows.derived,
      });
      setWriteOutcome({ identity: submittedIdentity, result: response });
    } catch (err) {
      setWriteOutcome({
        identity: submittedIdentity,
        error: {
          message:
            err instanceof OnboardingApiError
              ? err.message
              : 'Failed to write the catalog tables. Check the connection and try again.',
          code: err instanceof OnboardingApiError ? err.code : 'UNKNOWN',
        },
      });
    } finally {
      setWriting(false);
    }
  }, [canWrite, secretArn, tables, rows, writeIdentity]);

  // ── The two catalog tables ───────────────────────────────────────────
  // Rendered INSIDE both tabs (this same element is placed in each tab's
  // content), above that tab's action, so the user always reads the exact rows
  // before choosing to write or download them.
  const catalogTables = (
    <SpaceBetween size="l">
      <Table
        items={rows.base}
        columnDefinitions={BASE_COLUMN_DEFINITIONS}
        variant="embedded"
        header={
          <Header variant="h3" counter={`(${rows.base.length})`} description={tables.baseQualified}>
            Base metrics catalog
          </Header>
        }
        empty={
          <Box textAlign="center" color="inherit">
            <StatusIndicator type="warning">
              No base metrics defined — go back to the Metrics step.
            </StatusIndicator>
          </Box>
        }
      />
      <Table
        items={rows.derived}
        columnDefinitions={DERIVED_COLUMN_DEFINITIONS}
        variant="embedded"
        header={
          <Header
            variant="h3"
            counter={`(${rows.derived.length})`}
            description={
              rows.synthesizedCount > 0
                ? `${tables.derivedQualified} — includes ${rows.synthesizedCount} auto-added ROUND(<metric>,2) passthrough${rows.synthesizedCount === 1 ? '' : 's'} so Bluey can query each base metric directly`
                : tables.derivedQualified
            }
          >
            Derived metrics catalog
          </Header>
        }
        empty={
          <Box textAlign="center" color="inherit">
            <StatusIndicator type="warning">
              No derived metrics — Bluey can only query metrics listed here.
            </StatusIndicator>
          </Box>
        }
      />
    </SpaceBetween>
  );

  // ── Tab 1: write to Redshift ─────────────────────────────────────────
  const writeTab = (
    <SpaceBetween size="l">
      <Box variant="p">
        Creates the two tables in the schema below and inserts these rows. Existing tables are never
        overwritten — if either already contains rows, nothing is written and you can load the CSVs
        from the other tab instead.
      </Box>

      {!backendAvailable && (
        <Alert type="info">
          Writing to Redshift needs the backend service, which is not available in this environment.
          Use the “Download CSVs” tab and load them manually.
        </Alert>
      )}

      <FormField
        label="Write using"
        description="Which environment's secret ARN to connect with."
      >
        <Select
          selectedOption={SECRET_ENV_OPTIONS.find((o) => o.value === writeEnv) ?? null}
          onChange={({ detail }) => {
            setWriteEnv((detail.selectedOption.value as SecretEnv) ?? 'test');
            setWriteOutcome(null);
          }}
          options={SECRET_ENV_OPTIONS}
          ariaLabel="Catalog write environment"
        />
      </FormField>

      {catalogTables}

      <SpaceBetween size="s" direction="horizontal">
        <Button variant="primary" loading={writing} disabled={!canWrite} onClick={handleWrite}>
          Write catalogs
        </Button>
        {writeResult && (
          <StatusIndicator type="success">
            {`Wrote ${writeResult.rowsWritten?.base ?? 0} base and ${writeResult.rowsWritten?.derived ?? 0} derived rows`}
          </StatusIndicator>
        )}
        {alreadyWritten && (
          <Box variant="small">
            Already written. Edit the metrics or the schema above to enable another write.
          </Box>
        )}
      </SpaceBetween>

      {writeResult && (writeResult.tablesCreated?.length ?? 0) > 0 && (
        <Alert type="success" header="Catalog tables created">
          <SpaceBetween size="xs">
            <Box variant="p">Bluey can now resolve this tool&apos;s metrics. Created:</Box>
            <ul>
              {(writeResult.tablesCreated ?? []).map((name) => (
                <li key={name}>
                  <Box variant="code">{name}</Box>
                </li>
              ))}
            </ul>
          </SpaceBetween>
        </Alert>
      )}

      {writeResult && (writeResult.tablesReused?.length ?? 0) > 0 && (
        <Alert type="info" header="Some tables already existed">
          <SpaceBetween size="xs">
            <Box variant="p">
              These tables were already on the cluster and empty, so their rows were filled in rather
              than created. Worth a look if you did not expect a table with this name to exist — two
              tools whose names differ only in case or past 98 characters resolve to the same catalog:
            </Box>
            <ul>
              {(writeResult.tablesReused ?? []).map((name) => (
                <li key={name}>
                  <Box variant="code">{name}</Box>
                </li>
              ))}
            </ul>
          </SpaceBetween>
        </Alert>
      )}

      {writeError && (
        <Alert
          type={writeError.code === CATALOG_NOT_EMPTY_CODE ? 'warning' : 'error'}
          header={
            writeError.code === CATALOG_NOT_EMPTY_CODE
              ? 'Catalog tables already contain rows'
              : 'Could not write the catalog tables'
          }
          dismissible
          onDismiss={() => setWriteOutcome(null)}
          // A connection/network failure is usually transient, so offer the retry
          // in place rather than making the user hunt for the button again. A 409
          // is a deliberate refusal — retrying it would just fail identically.
          action={
            writeError.code === CATALOG_NOT_EMPTY_CODE ? undefined : (
              <Button onClick={handleWrite}>Retry</Button>
            )
          }
        >
          <SpaceBetween size="xs">
            <Box variant="p">{writeError.message}</Box>
            {writeError.code === CATALOG_NOT_EMPTY_CODE && (
              <Box variant="p">
                Nothing was written, so the existing catalog is untouched. If you did not create that
                table, another tool may resolve to the same name — catalog names come from the tool
                name lowercased and truncated to 98 characters, so two tools differing only in case
                or past that length collide. Either rename this tool, point it at a different schema
                above, or download the CSVs and reconcile the rows with your DBA.
              </Box>
            )}
          </SpaceBetween>
        </Alert>
      )}
    </SpaceBetween>
  );

  // ── Tab 2: download CSVs ─────────────────────────────────────────────
  const downloadTab = (
    <SpaceBetween size="l">
      <Box variant="p">
        The same rows as the write tab, as CSV files. Two formats, because they are not
        interchangeable — Redshift <Box variant="code">COPY</Box> has no comment syntax, so the
        AutoFlip directive would be ingested as data.
      </Box>

      <FormField
        label="File format"
        description={
          withAutoFlipHeader
            ? 'Includes the /** … **/ AutoFlip directive. Commit next to the other catalog files in the DaSFintechFDA-Bluey package (schema/) and let the loader pipeline apply it.'
            : 'Plain CSV — a header row plus data. Use this for a manual COPY (with IGNOREHEADER 1) or to open in a spreadsheet.'
        }
      >
        <Select
          selectedOption={CSV_FORMAT_OPTIONS.find((o) => o.value === csvFormat) ?? null}
          onChange={({ detail }) =>
            setCsvFormat((detail.selectedOption.value as CsvFormat) ?? 'autoflip')
          }
          options={CSV_FORMAT_OPTIONS}
          ariaLabel="CSV file format"
        />
      </FormField>

      {catalogTables}

      <SpaceBetween size="s" direction="horizontal">
        <Button
          iconName="download"
          disabled={rows.base.length === 0}
          onClick={() => downloadFile(baseFileName, baseCsv, 'text/csv')}
        >
          Download {baseFileName}
        </Button>
        <Button
          iconName="download"
          disabled={rows.derived.length === 0}
          onClick={() => downloadFile(derivedFileName, derivedCsv, 'text/csv')}
        >
          Download {derivedFileName}
        </Button>
      </SpaceBetween>

      <Box variant="small">
        {`${baseFileName} — ${rows.base.length} row${rows.base.length === 1 ? '' : 's'} · `}
        {`${derivedFileName} — ${rows.derived.length} row${rows.derived.length === 1 ? '' : 's'}`}
      </Box>
    </SpaceBetween>
  );

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Bluey resolves every requested metric against these two Redshift tables — the YAML above only names them. Create them before the tool runs."
        >
          Metric catalog metadata
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Rendered above the other warnings and phrased for maximum risk: this
            no longer blocks the write, so it is dismissible — but the catalog is
            create-only and unrepairable, so this warning is the only safeguard
            left against a carve-out reaching Redshift. */}
        {versionAxisErrors.length > 0 && !versionAxisDismissed && (
          <Alert
            type="warning"
            header="Possible version-axis metric — may return wrong numbers at query time"
            dismissible
            onDismiss={() => setVersionAxisDismissed(true)}
          >
            <ul>
              {versionAxisErrors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {rowWarnings.length > 0 && (
          <Alert type="warning" header="Check these metric definitions">
            <ul>
              {rowWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </Alert>
        )}

        <FormField
          label="Catalog schema"
          description={
            'Where the two catalog tables live. Every FDA catalog uses bluey_input; change it only ' +
            'for a cluster with a different convention. This also sets base_metrics_catalog / ' +
            'derived_metrics_catalog in the YAML above — re-download the YAML if you change it.'
          }
          errorText={
            schemaValid
              ? undefined
              : 'Must start with a letter or underscore and contain only letters, numbers, and underscores.'
          }
        >
          <Input
            value={config.catalogSchema}
            onChange={({ detail }) => dispatch({ type: 'SET_CATALOG_SCHEMA', value: detail.value })}
            ariaLabel="Catalog schema"
          />
        </FormField>

        {/* When the backend is unavailable the write tab is inert, so lead with
            the download tab rather than landing the user on a dead default. */}
        <Tabs
          tabs={
            backendAvailable
              ? [
                  { id: 'write', label: 'Write to Redshift', content: writeTab },
                  { id: 'download', label: 'Download CSVs', content: downloadTab },
                ]
              : [
                  { id: 'download', label: 'Download CSVs', content: downloadTab },
                  { id: 'write', label: 'Write to Redshift', content: writeTab },
                ]
          }
        />
      </SpaceBetween>
    </Container>
  );
};

export default CatalogMetadataSection;
